import { useStore } from 'vuex'
import { computed, getCurrentInstance } from 'vue'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'
import usePrompts from '@/composables/usePrompts'
import { DataType, SchemaType } from '@/constants/schema'
import { runAiGeneration } from '@/utils/helpHero'

let pendingSchemaChange = null

const applyPendingChanges = (schemas) => {
  if (pendingSchemaChange) {
    pendingSchemaChange(schemas)
    pendingSchemaChange = null
  }
}

const shouldDeferSchemaUpdate = (e) => {
  return !!e.relatedTarget?.classList?.contains('defers-schema-blur')
}

const useEntityActions = () => {
  const store = useStore()
  const { proxy } = getCurrentInstance()
  const { getFieldExampleData } = usePrompts()
  const workflow = computed(() => store.state.workflow.workflow)

  const moveRight = async (schemaId, fieldIndex) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    applyPendingChanges(newSchemas)
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    if (newSchemas[schemaIndex].fields.length > (fieldIndex + 1)) {
      const field = newSchemas[schemaIndex].fields[fieldIndex]

      newSchemas[schemaIndex].fields[fieldIndex] = newSchemas[schemaIndex].fields[fieldIndex + 1]
      newSchemas[schemaIndex].fields[fieldIndex + 1] = field
      await workflowApi.updateSchemas({
        schemas: newSchemas,
        workflow: workflow.value
      })
    }
  }

  const moveLeft = async (schemaId, fieldIndex) => {
    if (fieldIndex < 1) {
      return
    }

    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    applyPendingChanges(newSchemas)
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const field = newSchemas[schemaIndex].fields[fieldIndex]
    newSchemas[schemaIndex].fields[fieldIndex] = newSchemas[schemaIndex].fields[fieldIndex - 1]
    newSchemas[schemaIndex].fields[fieldIndex - 1] = field

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const blurEntityName = async (e, schemaId) => {
    const schemas = workflow.value.schemas || []
    const schemaIndex = schemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const newName = e.target.innerText.trim()
    if (schemas[schemaIndex].name === newName) {
      return
    }

    const currentEntity = schemas[schemaIndex]
    const hasBoundedContext = !!currentEntity.boundedContext
    const isDuplicate = hasBoundedContext && schemas.some((s) =>
      s.id !== schemaId &&
      s.type === SchemaType.ENTITY &&
      s.boundedContext === currentEntity.boundedContext &&
      s.name.toLowerCase() === newName.toLowerCase()
    )

    if (isDuplicate) {
      e.target.innerText = currentEntity.name
      const bcName = (workflow.value.boundedContexts || []).find(
        (bc) => bc.id === currentEntity.boundedContext)?.name || 'this bounded context'
      proxy.$store.commit('snackbar/showMessage', {
        content: `An entity with the name "${newName}" already exists in ${bcName}`,
        timeout: 4000,
        color: 'red'
      }, { root: true })

      return
    }

    const applyNameChange = (targetSchemas) => {
      const idx = targetSchemas.findIndex((s) => s.id === schemaId)
      if (idx !== -1) {
        targetSchemas[idx].name = newName
      }
    }

    if (shouldDeferSchemaUpdate(e)) {
      pendingSchemaChange = applyNameChange

      return
    }

    const newSchemas = safeStructuredClone(schemas)
    applyNameChange(newSchemas)
    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const blurTableCell = async (e, schemaId, fieldIndex, fieldName, indexAttribute = -1) => {
    const schemas = workflow.value.schemas || []
    const schemaIndex = schemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    let oldVal
    const newVal = e.target.innerText?.trim() ?? ''
    if (indexAttribute !== null && indexAttribute >= 0) {
      oldVal = schemas[schemaIndex].fields[fieldIndex][fieldName][indexAttribute]
    } else {
      oldVal = schemas[schemaIndex].fields[fieldIndex][fieldName]
    }
    if (oldVal === undefined && newVal === '') {
      return
    }
    if (oldVal !== newVal) {
      const applyCellChange = (targetSchemas) => {
        const idx = targetSchemas.findIndex((s) => s.id === schemaId)
        if (idx === -1) {
          return
        }
        if (indexAttribute !== null && indexAttribute >= 0) {
          targetSchemas[idx].fields[fieldIndex][fieldName][indexAttribute] = newVal
        } else {
          targetSchemas[idx].fields[fieldIndex][fieldName] = newVal
        }
      }

      if (shouldDeferSchemaUpdate(e)) {
        pendingSchemaChange = applyCellChange

        return
      }

      const newSchemas = safeStructuredClone(schemas)
      applyCellChange(newSchemas)
      await workflowApi.updateSchemas({
        schemas: newSchemas,
        workflow: workflow.value
      })
    }
  }

  const addColumn = async (schemaId) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    applyPendingChanges(newSchemas)
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    newSchemas[schemaIndex].fields.push({
      name: 'NEW FIELD ' + (newSchemas[schemaIndex].fields.length + 1),
      cardinality: 'one-to-one',
      exampleData: [''],
      dataType: 'string',
      relatedEntityId: null
    })
    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const deleteEntity = async (schemaId) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    applyPendingChanges(newSchemas)
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    newSchemas.splice(schemaIndex, 1)

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const deleteEntityField = async (schemaId, fieldIndex) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    applyPendingChanges(newSchemas)
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    newSchemas[schemaIndex].fields.splice(fieldIndex, 1)

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const selectRelatedEntity = async (relatedEntity, schemaId, fieldIndex) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    applyPendingChanges(newSchemas)
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    newSchemas[schemaIndex].fields[fieldIndex].relatedEntityId = relatedEntity?.id || null

    if (relatedEntity) {
      newSchemas[schemaIndex].fields[fieldIndex].dataType = DataType.OBJECT
    } else {
      newSchemas[schemaIndex].fields[fieldIndex].dataType = DataType.STRING
    }

    if (!newSchemas[schemaIndex].fields[fieldIndex].cardinality) {
      newSchemas[schemaIndex].fields[fieldIndex].cardinality = 'one-to-one'
    }

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const selectCardinality = async (cardinality, schemaId, relatedEntityId) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    applyPendingChanges(newSchemas)
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    newSchemas[schemaIndex].fields.forEach((field, index) => {
      if (field.relatedEntityId === relatedEntityId) {
        newSchemas[schemaIndex].fields[index].cardinality = cardinality || null
      }
    })

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  // const setPrimaryKey = async (schemaId, fieldIndex) => {
  //   const newSchemas = safeStructuredClone(workflow.value.schemas || [])
  //   const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
  //   if (schemaIndex === -1) {
  //     return
  //   }
  //
  //   newSchemas[schemaIndex].fields = newSchemas[schemaIndex].fields.map((field, index) => {
  //     if (index === fieldIndex) {
  //       const isSettingAsPrimaryKey = !field.primaryKey
  //
  //       const shouldChangeDataType = isSettingAsPrimaryKey &&
  //         field.dataType !== DataType.STRING &&
  //         field.dataType !== DataType.NUMBER
  //
  //       return {
  //         ...field,
  //         primaryKey: isSettingAsPrimaryKey,
  //         dataType: shouldChangeDataType ? DataType.STRING : field.dataType,
  //         relatedEntityId: shouldChangeDataType ? null : field.relatedEntityId
  //       }
  //     }
  //
  //     return { ...field, primaryKey: false }
  //   })
  //
  //   await workflowApi.updateSchemas({
  //     schemas: newSchemas,
  //     workflow: workflow.value
  //   })
  // }

  const setDataType = async (schemaId, fieldIndex, dataType, relatedEntityId = null) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    applyPendingChanges(newSchemas)
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }
    const field = newSchemas[schemaIndex].fields[fieldIndex]

    field.dataType = dataType
    field.relatedEntityId = relatedEntityId

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const setFieldCardinality = async (schemaId, fieldIndex, cardinality) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    applyPendingChanges(newSchemas)
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }
    const field = newSchemas[schemaIndex].fields[fieldIndex]

    field.cardinality = cardinality

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const getFieldAtPath = (fields, path) => {
    let current = { fields }
    for (const index of path) {
      if (!current.fields || index < 0 || index >= current.fields.length) {
        return null
      }
      current = current.fields[index]
    }
    return current
  }

  const mutateFieldTagsAtPath = async (schemaId, path, mutate) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    applyPendingChanges(newSchemas)
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const field = getFieldAtPath(newSchemas[schemaIndex].fields, path)
    if (!field) {
      return
    }

    const tags = new Set(field.tags || [])
    mutate(tags)
    field.tags = [...tags]

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  // KEY implies REQUIRED; an optional field can't be a key.
  const setFieldMandatoryAtPath = (schemaId, path, isMandatory) => {
    return mutateFieldTagsAtPath(schemaId, path, (tags) => {
      if (isMandatory) {
        tags.add('REQUIRED')
      } else {
        tags.delete('REQUIRED')
        tags.delete('KEY')
      }
    })
  }

  const setFieldMandatory = (schemaId, fieldIndex, isMandatory) => {
    return setFieldMandatoryAtPath(schemaId, [fieldIndex], isMandatory)
  }

  const setSubFieldMandatory = (schemaId, fieldIndex, subFieldIndex, isMandatory) => {
    return setFieldMandatoryAtPath(schemaId, [fieldIndex, subFieldIndex], isMandatory)
  }

  const setFieldKeyAtPath = (schemaId, path, isKey) => {
    return mutateFieldTagsAtPath(schemaId, path, (tags) => {
      if (isKey) {
        tags.add('KEY')
        tags.add('REQUIRED')
      } else {
        tags.delete('KEY')
      }
    })
  }

  const setFieldKey = (schemaId, fieldIndex, isKey) => {
    return setFieldKeyAtPath(schemaId, [fieldIndex], isKey)
  }

  const setSubFieldKey = (schemaId, fieldIndex, subFieldIndex, isKey) => {
    return setFieldKeyAtPath(schemaId, [fieldIndex, subFieldIndex], isKey)
  }

  // Its saves are the AI's, not the person's own edits (activation tracking).
  const generateExampleData = (schemaId, fieldIndex) => runAiGeneration(() => writeExampleData(schemaId, fieldIndex))

  const writeExampleData = async (schemaId, fieldIndex) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    applyPendingChanges(newSchemas)
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const entity = newSchemas[schemaIndex]

    if (!entity || !entity.fields) {
      return
    }

    proxy.$loadingState.start()

    try {
      const field = entity.fields[fieldIndex]

      const response = await getFieldExampleData(entity, field)

      if (!response?.exampleData) {
        proxy.$loadingState.stop()
        return
      }

      newSchemas[schemaIndex].fields[fieldIndex].exampleData = response.exampleData

      await workflowApi.updateSchemas({
        schemas: newSchemas,
        workflow: workflow.value
      })
    } catch (error) {
      if (error.name !== 'AbortError') {
        await store.dispatch('workflow/openRetryDialog', {
          failedItems: [{ type: 'exampleData', name: 'Generate Example Data' }],
          retryCallback: () => generateExampleData(schemaId, fieldIndex)
        })
      }
    } finally {
      proxy.$loadingState.stop()
    }
  }

  const deleteExampleDataRow = async (schemaId, rowIndex) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    applyPendingChanges(newSchemas)
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const entity = newSchemas[schemaIndex]

    if (!entity || !entity.fields) {
      return
    }

    // Remove the element at rowIndex from each field's exampleData array
    entity.fields.forEach((field) => {
      if (
        field.exampleData &&
        Array.isArray(field.exampleData) &&
        rowIndex >= 0 &&
        rowIndex < field.exampleData.length
      ) {
        field.exampleData.splice(rowIndex, 1)
      }
    })

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const addFieldToEntityByName = async (entityId, fieldName) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    applyPendingChanges(newSchemas)
    const schemaIndex = newSchemas.findIndex((s) => s.id === entityId)
    if (schemaIndex === -1) {
      return
    }

    const existingField = newSchemas[schemaIndex].fields?.find((f) => f.name === fieldName)
    if (existingField) {
      return
    }

    if (!newSchemas[schemaIndex].fields) {
      newSchemas[schemaIndex].fields = []
    }

    newSchemas[schemaIndex].fields.push({
      name: fieldName,
      cardinality: 'one-to-one',
      exampleData: [''],
      dataType: 'string',
      relatedEntityId: null
    })

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })

    proxy.$store.commit('snackbar/showMessage', {
      content: `Added field "${fieldName}" to entity`,
      timeout: 3000,
      color: 'success'
    }, { root: true })
  }

  return {
    moveRight,
    moveLeft,
    blurEntityName,
    blurTableCell,
    addColumn,
    deleteEntity,
    deleteEntityField,
    selectRelatedEntity,
    selectCardinality,
    setDataType,
    setFieldCardinality,
    setFieldMandatoryAtPath,
    setFieldMandatory,
    setSubFieldMandatory,
    setFieldKeyAtPath,
    setFieldKey,
    setSubFieldKey,
    generateExampleData,
    deleteExampleDataRow,
    addFieldToEntityByName
  }
}

export default useEntityActions
